<script setup lang="ts">
import { pushMenuClickCtaEvent } from "~/utils/analytics/clickCta"
import { useMenuStore } from "~/stores/menu"
import { storeToRefs } from "pinia"
import { MenuItem } from "~~/server/api/menu/sidebar/getSidebarOtherMenuItems"

const { data: otherItems, error: otherItemsError } = await useFetch(
  "/api/menu/sidebar/getSidebarOtherMenuItems"
)

const menuStore = useMenuStore()
const { selectedOtherMenuItem } = storeToRefs(menuStore)

const handleTopLevelClick = (item: MenuItem) => {
  pushMenuClickCtaEvent(item.label)
  if (item.subItems?.length) {
    menuStore.selectOtherMenuItemHandler(item)
  } else {
    menuStore.openCloseSidebar(false)
  }
}

const handleSubItemClick = (label: string) => {
  pushMenuClickCtaEvent(label)
  menuStore.openCloseSidebar(false)
}
</script>

<template>
  <!-- Sub-menu view -->
  <div
    v-if="selectedOtherMenuItem"
    class="
      content-sidebar-desktop
      relative
      min-h-full
      pt-10
      transition-all
      duration-300
      ease-in-out
    "
  >
    <Transition name="slide-left">
      <div
        class="
          navigation-section
          bg-slate-80
          absolute
          left-0
          top-0
          z-0
          w-full
          px-4
          delay-200
        "
      >
        <UtilsButton
          theme="link-white"
          class="custom-btn-no-underline custom-btn-pig-medium"
          :text="$t('hamburgerMenu.menuContent.allCategories')"
          stop
          @click="menuStore.goBackOtherMenu()"
        >
          <template #icon>
            <UtilsIcon name="ArrowLeft.svg" color="white" class="h-6 w-6" />
          </template>
        </UtilsButton>
      </div>
    </Transition>

    <div class="title-section-category p-4">
      <UtilsButton
        :text="selectedOtherMenuItem.label"
        class="
          custom-btn-hamburger-title
          category
          transition-all
          duration-300
          ease-in-out
        "
        :path="selectedOtherMenuItem.path"
        :is-disabled="!selectedOtherMenuItem.path"
        @click="selectedOtherMenuItem.path && menuStore.openCloseSidebar(false)"
      />
    </div>

    <div class="py-5 pl-4">
      <div class="flex flex-wrap justify-start gap-y-7">
        <div
          v-for="(subItem, key) in selectedOtherMenuItem.subItems"
          :key="key"
          class="relative flex w-full flex-wrap justify-between pr-4"
          @click="handleSubItemClick(subItem.label)"
        >
          <UtilsButton
            :text="subItem.label"
            theme="ghost-green"
            :path="subItem.path"
            class="
              custom-btn-pig-semibold-link
              custom-btn-no-padding
              custom-btn-start
              z-[1]
              w-full
            "
          />
          <UtilsIcon
            name="ChevronRight.svg"
            class="absolute right-4 top-0 z-0 h-6 w-6"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Root view: top-level list -->
  <div v-else class="other-links py-5 pl-4">
    <div class="flex flex-wrap justify-start gap-y-7">
      <div
        v-for="(link, key) in otherItems"
        :key="key"
        class="relative flex w-full flex-wrap justify-between pr-4"
        @click="handleTopLevelClick(link)"
      >
        <UtilsButton
          :text="link.label"
          theme="ghost-green"
          :path="link.subItems?.length ? undefined : link.path"
          class="
            custom-btn-pig-semibold-link custom-btn-no-padding custom-btn-start
            z-[1]
            w-full
          "
        />
        <UtilsIcon
          name="ChevronRight.svg"
          class="absolute right-4 top-0 z-0 h-6 w-6"
        />
      </div>
    </div>
  </div>
</template>
